import React, { useCallback, useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { supabase } from '../../lib/supabase'
import { useAuth } from '../../lib/AuthContext'
import { ADMIN_AGENTS_SETTINGS_PATH } from '../../lib/dashboardNav'
import { groupCatalogByCategory } from '../../lib/agentCategories'
import { getAgentCardMeta } from '../../lib/agentCardMeta'
import { formatCfgUpdated } from '../../lib/basecampAgentCards'
import { AGENT_BADGE_STYLES } from '../../lib/useAgentPipelineReadiness'
import AgentEditModal from './AgentEditModal'
import AgentRunModal from './AgentRunModal'
import AgentWorkspaceSection from './AgentWorkspaceSection'
import './agentsPage.css'

const AGENT_TASK_CREATOR = 'basecamp_task_creator'
const KNOWLEDGE_ASSISTANT_KEY = 'knowledge_assistant'

function AdminAgentCard({
  row,
  categoryKey,
  showStepBadge,
  readOnly,
  runDisabled,
  runDisabledTitle,
  hideRun,
  openChatHref,
  onEdit,
  onRun,
  cornerBadges = [],
}) {
  const { icon: AgentIcon, accent } = getAgentCardMeta(row.agent_key, categoryKey)
  const iconClass = `agents-admin-card-icon agents-admin-card-icon--${accent}`

  const badges = [...cornerBadges]
  if (row.is_active === false && !badges.includes('inactive')) badges.push('inactive')

  const cardClass = [
    'agents-admin-card',
    row.is_active === false ? 'agents-admin-card--inactive' : '',
  ]
    .filter(Boolean)
    .join(' ')

  return (
    <article className={cardClass}>
      <div className="agents-admin-card-header">
        <div className={iconClass}>
          <AgentIcon size={22} strokeWidth={2} />
        </div>
        <div className="agents-admin-card-heading">
          <h3 className="agents-admin-card-title">{row.display_name || row.agent_key}</h3>
          <div className="agents-admin-card-badges">
            {showStepBadge && row.step_order != null ? (
              <span className="agents-admin-card-badge agents-admin-card-badge--step">
                Step {row.step_order}
              </span>
            ) : null}
            {badges.map((key) => {
              const style = AGENT_BADGE_STYLES[key]
              if (!style) return null
              return (
                <span
                  key={key}
                  className="agents-admin-card-badge"
                  style={{ background: style.bg, color: style.color }}
                >
                  {style.label}
                </span>
              )
            })}
          </div>
        </div>
      </div>

      <div className="agents-admin-card-body">
        <p className="agents-admin-card-desc">{row.description || 'No description.'}</p>

        {row.agent_key === AGENT_TASK_CREATOR ? (
          <p className="agents-admin-card-meta">
            Requires analyzer output — run Step 1 first, or use a saved analyzer run per project.
          </p>
        ) : null}

        {row.agent_key === KNOWLEDGE_ASSISTANT_KEY ? (
          <p className="agents-admin-card-meta">
            Powers the chat on{' '}
            <Link to="/knowledge" style={{ color: '#2563eb', fontWeight: 600 }}>
              /knowledge
            </Link>
            .
          </p>
        ) : null}

        <p className="agents-admin-card-meta">
          Updated: <strong>{formatCfgUpdated(row.updated_at)}</strong>
        </p>

        {row.is_active === false ? (
          <p className="agents-admin-card-meta agents-admin-card-meta--warn">
            Disabled — will not run when inactive.
          </p>
        ) : null}
      </div>

      {!readOnly ? (
        <div className="agents-admin-card-actions">
          <button type="button" onClick={() => onEdit()} className="agents-admin-card-btn agents-admin-card-btn--secondary">
            Edit
          </button>
          {hideRun && openChatHref ? (
            <Link to={openChatHref} className="agents-admin-card-btn agents-admin-card-btn--primary">
              Open chat
            </Link>
          ) : (
            <button
              type="button"
              title={runDisabled ? runDisabledTitle : 'Run this agent stage with real data (preview)'}
              disabled={runDisabled}
              onClick={() => onRun()}
              className="agents-admin-card-btn agents-admin-card-btn--primary"
            >
              Run
            </button>
          )}
        </div>
      ) : (
        <p className="agents-admin-card-readonly">
          Super admin can edit agents. Digest automation and tests are on the{' '}
          <Link to={`${ADMIN_AGENTS_SETTINGS_PATH}?tab=automation`}>Automation Settings</Link> tab.
        </p>
      )}
    </article>
  )
}

/**
 * @param {Object} props
 * @param {'admin' | 'workspace'} [props.variant]
 * @param {Record<string, string[]>} [props.badgesByAgentKey]
 * @param {Record<string, string>} [props.statusHintByAgentKey]
 * @param {React.ReactNode} [props.headerExtra]
 */
export default function AgentPipelineGrid({
  variant = 'admin',
  badgesByAgentKey = {},
  statusHintByAgentKey = {},
  headerExtra = null,
}) {
  const { isSuperAdmin } = useAuth()
  const isWorkspace = variant === 'workspace'
  const [loading, setLoading] = useState(true)
  const [err, setErr] = useState('')
  const [msg, setMsg] = useState('')
  const [categoryGroups, setCategoryGroups] = useState([])
  const [adminCfgRows, setAdminCfgRows] = useState([])
  const [pipelineByAgentKey, setPipelineByAgentKey] = useState({})
  const [editModalAgentKey, setEditModalAgentKey] = useState(null)
  const [runModalAgentKey, setRunModalAgentKey] = useState(null)
  const [sessionAnalyzerByLinkId, setSessionAnalyzerByLinkId] = useState({})
  const [hasAnyAnalyzerRun, setHasAnyAnalyzerRun] = useState(false)

  const load = useCallback(async () => {
    setLoading(true)
    setErr('')
    try {
      if (isWorkspace) {
        const { data: pipelines, error: pErr } = await supabase
          .from('ai_pipelines')
          .select('*')
          .eq('is_enabled', true)
          .order('category_sort', { ascending: true })
          .order('pipeline_sort', { ascending: true })

        if (pErr) throw pErr

        const pipeList = pipelines?.length ? pipelines : []
        const ids = pipeList.map((p) => p.id)
        let agents = []
        if (ids.length) {
          const { data: aRows, error: aErr } = await supabase
            .from('basecamp_ai_agent_configs')
            .select('*')
            .in('pipeline_id', ids)
            .order('step_order', { ascending: true })
          if (aErr) throw aErr
          agents = aRows || []
        }

        setCategoryGroups(groupCatalogByCategory(pipeList, agents))

        const workspacePipeMap = {}
        for (const p of pipeList) {
          for (const a of agents.filter((ag) => ag.pipeline_id === p.id)) {
            workspacePipeMap[a.agent_key] = p
          }
        }
        setPipelineByAgentKey(workspacePipeMap)
      } else {
        // Admin mode: load ALL enabled pipelines + all their agents, grouped by category
        const { data: pipelines, error: pErr } = await supabase
          .from('ai_pipelines')
          .select('*')
          .eq('is_enabled', true)
          .order('category_sort', { ascending: true })
          .order('pipeline_sort', { ascending: true })
        if (pErr) throw pErr

        const pipeList = pipelines || []
        const ids = pipeList.map((p) => p.id)
        let allAgents = []
        if (ids.length) {
          const { data: aRows, error: aErr } = await supabase
            .from('basecamp_ai_agent_configs')
            .select('*')
            .in('pipeline_id', ids)
            .order('step_order', { ascending: true })
          if (aErr) throw aErr
          allAgents = aRows || []
        }

        const groups = groupCatalogByCategory(pipeList, allAgents)
        setCategoryGroups(groups)

        // Keep adminCfgRows for edit/run modal lookups
        setAdminCfgRows(allAgents)

        // Build pipeline lookup by agent key for AgentRunModal routing
        const pipeMap = {}
        for (const p of pipeList) {
          for (const a of allAgents.filter((ag) => ag.pipeline_id === p.id)) {
            pipeMap[a.agent_key] = p
          }
        }
        setPipelineByAgentKey(pipeMap)

      }

      if (isSuperAdmin) {
        const { count } = await supabase
          .from('basecamp_agent_runs')
          .select('id', { count: 'exact', head: true })
          .eq('agent_name', 'basecamp_data_analyzer')
          .eq('status', 'success')
        setHasAnyAnalyzerRun((count ?? 0) > 0)
      }
    } catch (e) {
      setErr(e?.message || String(e))
    } finally {
      setLoading(false)
    }
  }, [isSuperAdmin, isWorkspace])

  useEffect(() => {
    load()
  }, [load])

  const handleAgentSaved = (updatedRow) => {
    if (updatedRow) {
      setAdminCfgRows((prev) => {
        const rest = prev.filter((r) => r.agent_key !== updatedRow.agent_key)
        return [...rest, updatedRow].sort((a, b) => (a.step_order ?? 99) - (b.step_order ?? 99))
      })
    } else {
      void load()
    }
  }

  const handleSessionAnalyzerSaved = useCallback((linkId, payload) => {
    setSessionAnalyzerByLinkId((prev) => ({ ...prev, [linkId]: payload }))
    setHasAnyAnalyzerRun(true)
  }, [])

  const allAgents = isWorkspace
    ? categoryGroups.flatMap((g) => g.pipelines.flatMap((p) => p.agents))
    : adminCfgRows

  const editRow = allAgents.find((r) => r.agent_key === editModalAgentKey)
  const runRow = allAgents.find((r) => r.agent_key === runModalAgentKey)
  const hasSessionAnalyzer = Object.keys(sessionAnalyzerByLinkId).length > 0
  const taskCreatorRunDisabled = !hasAnyAnalyzerRun && !hasSessionAnalyzer

  return (
    <>
      {headerExtra}

      {loading ? <p style={{ color: '#64748b' }}>Loading agents…</p> : null}
      {err ? <p style={{ color: '#b91c1c' }}>{err}</p> : null}
      {msg ? <p style={{ color: '#15803d' }}>{msg}</p> : null}

      {!loading && isWorkspace ? (
        categoryGroups.length === 0 ? (
          <p style={{ color: '#64748b', fontSize: 14 }}>No runnable agents are configured yet.</p>
        ) : (
          categoryGroups.map((group) => (
            <AgentWorkspaceSection
              key={group.categoryKey}
              meta={group.meta}
              categoryKey={group.categoryKey}
              pipelines={group.pipelines}
              badgesByAgentKey={badgesByAgentKey}
              statusHintByAgentKey={statusHintByAgentKey}
              isSuperAdmin={isSuperAdmin}
              hasAnyAnalyzerRun={hasAnyAnalyzerRun}
              hasSessionAnalyzer={hasSessionAnalyzer}
              onRun={setRunModalAgentKey}
            />
          ))
        )
      ) : null}

      {!loading && !isWorkspace ? (
        categoryGroups.length === 0 ? (
          <p style={{ color: '#64748b', fontSize: 14 }}>No agent pipelines found.</p>
        ) : (
          categoryGroups.map((group) => (
            <div key={group.categoryKey} style={{ marginBottom: 32 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 14 }}>
                {group.meta.icon
                  ? React.createElement(group.meta.icon, { size: 18, color: '#475569' })
                  : null}
                <h3 style={{ margin: 0, fontSize: 15, fontWeight: 700, color: '#0f172a' }}>
                  {group.meta.label}
                </h3>
                {group.meta.description ? (
                  <span style={{ fontSize: 12, color: '#94a3b8', fontWeight: 400 }}>
                    — {group.meta.description}
                  </span>
                ) : null}
              </div>
              {group.pipelines.map(({ agents: pipeAgents }) => (
                <div key={pipeAgents[0]?.pipeline_id || group.categoryKey} className="agents-admin-grid">
                  {pipeAgents.map((row) => (
                    <AdminAgentCard
                      key={row.agent_key}
                      row={row}
                      categoryKey={group.categoryKey}
                      showStepBadge={pipeAgents.length > 1}
                      readOnly={!isSuperAdmin}
                      runDisabled={row.agent_key === AGENT_TASK_CREATOR && taskCreatorRunDisabled}
                      runDisabledTitle="Run the Data Analyzer first (or use a saved analyzer run in the modal)"
                      hideRun={row.agent_key === KNOWLEDGE_ASSISTANT_KEY}
                      openChatHref={row.agent_key === KNOWLEDGE_ASSISTANT_KEY ? '/knowledge' : null}
                      onEdit={() => setEditModalAgentKey(row.agent_key)}
                      onRun={() => setRunModalAgentKey(row.agent_key)}
                    />
                  ))}
                </div>
              ))}
            </div>
          ))
        )
      ) : null}

      {!isWorkspace && editModalAgentKey && isSuperAdmin && editRow ? (
        <AgentEditModal
          agentKey={editModalAgentKey}
          cfgRow={editRow}
          onClose={() => setEditModalAgentKey(null)}
          onSaved={handleAgentSaved}
          setPageMsg={setMsg}
          setPageErr={setErr}
        />
      ) : null}

      {runModalAgentKey && isSuperAdmin && runRow ? (
        <AgentRunModal
          agentKey={runModalAgentKey}
          agentLabel={runRow.display_name || runRow.agent_key}
          pipeline={pipelineByAgentKey[runModalAgentKey] || null}
          sessionAnalyzerByLinkId={sessionAnalyzerByLinkId}
          onSessionAnalyzerSaved={handleSessionAnalyzerSaved}
          onClose={() => setRunModalAgentKey(null)}
        />
      ) : null}
    </>
  )
}
